import { MprcContainer } from "components";
import { MprcFilterCount } from "components/filter";
import { MprcRequestErrorReport } from "components/MprcRequestErrorReport";
import { MprcRibbonListCardsWrapContainer } from "components/MprcRibbonList";
import { MprcSpinnerIndicator } from "components/MprcSpinnerIndicator";
import React, { useEffect, useImperativeHandle, useState } from 'react';

export type SearchableListHandle = {
    reset: () => void;
    replaceCard: (index: number, card: React.ReactNode) => void;
};

interface SearchableListProps extends React.PropsWithChildren {
    gapWidth: number;
    noRecordsMessage: string;
    filterText: (count: number) => string;
    onResetFilter: () => void;
    onGetCards: () => Promise<React.ReactNode[]>;
}

const SearchableListComponent: React.ForwardRefRenderFunction<SearchableListHandle, SearchableListProps> = (props, forwardRef) => {
    const { gapWidth, noRecordsMessage, filterText, onResetFilter, onGetCards } = props;
    const [loading, setLoading] = useState<boolean>(true);
    const [cards, setCards] = useState<React.ReactNode[] | null>(null);
    const [viewError, setViewError] = useState<any>();

    /////////////////////////////////////////////////////////////////////////////////////////////////////////

    useImperativeHandle(forwardRef, () => ({
        reset: () => { setLoading(true); },
        replaceCard: (index: number, card: React.ReactNode) => {
            if (cards != null) {
                setCards(cards.map((c, i) => i != index ? c : card));
            }
        }
    }));

    /////////////////////////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        async function load() {
            setLoading(true);
            try {
                const c = await onGetCards();
                setCards(c);
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setLoading(false);
            }
        }
        if (loading) load();
    }, [loading]);

    return viewError
        ? <MprcRequestErrorReport error={viewError} />
        : (
            <>
                <MprcContainer>
                    <MprcFilterCount
                        loading={loading}
                        isEmpty={React.Children.count(cards) == 0}
                        countText={noRecordsMessage}
                        filter={{
                            active: true,
                            text: filterText(React.Children.count(cards)),
                            onResetFilter: onResetFilter
                        }}
                    />
                </MprcContainer>

                {
                    loading
                        ? <MprcSpinnerIndicator />
                        : <MprcRibbonListCardsWrapContainer gapWidth={gapWidth}>
                            {cards}
                        </MprcRibbonListCardsWrapContainer>
                }
            </>
        );
};

export const SearchableList = React.forwardRef(SearchableListComponent);
